import { heightAt, SIZE } from '../board.js';
import { RuleError } from '../errors.js';
import { mulberry32, randomInt } from '../rng.js';
import { ARMY_SIZE, resolveIceConfig } from './config.js';
import { generateLake, RUNESTONES, runestonesConnected, HOLE, isHole, setHeight } from './lake.js';
import { toIceNotation } from './notation.js';
import { legalIceActions, pathTo, reach, shoveFrom, shoveOptions } from './moves.js';
import type {
  Crack,
  IceAction,
  IceConfig,
  IceEvent,
  IceResult,
  IceState,
  IceWinReason,
  Lake,
  Side,
  Warrior,
} from './types.js';

/** Most Glory Brand can earn in one turn: one per runestone. */
export const MAX_GLORY_PER_TURN = RUNESTONES.length;
const CRACK_ATTEMPTS = 200;

export interface NewIceGameOptions {
  /** Lake #seed. Ignored when `lake` is given. */
  seed?: number;
  lake?: Lake;
  /** Seed of the crack draws. Live games keep it secret until the end. */
  crackSeed?: number;
  config?: Partial<IceConfig>;
}

export function newIceGame(options: NewIceGameOptions = {}): IceState {
  const lake = options.lake ?? generateLake(options.seed ?? 1);
  return {
    config: resolveIceConfig(options.config),
    lake,
    heights: lake.heights.map((row) => [...row]),
    crackSeed: options.crackSeed ?? lake.seed,
    phase: 'placing',
    turn: 'brand',
    round: 1,
    warriors: [],
    glory: 0,
    cracks: [],
    winner: null,
    winReason: null,
    moves: [],
  };
}

export function otherSide(side: Side): Side {
  return side === 'brand' ? 'rime' : 'brand';
}

/** Runestones where Brand has more warriors than Rime. */
/** Who holds each runestone, west to east: the side with more warriors on its floe, or null. */
export function runestoneHolders(state: IceState): (Side | null)[] {
  return RUNESTONES.map((stone) => {
    let lead = 0;
    for (const w of state.warriors) {
      if (stone.some((s) => s.r === w.r && s.c === w.c)) lead += w.side === 'brand' ? 1 : -1;
    }
    return lead > 0 ? 'brand' : lead < 0 ? 'rime' : null;
  });
}

/** Runestones Brand holds: the Glory it earns after its turn. */
export function runestonesHeld(state: IceState): number {
  return runestoneHolders(state).filter((side) => side === 'brand').length;
}

function warriorById(state: IceState, id: string): Warrior {
  const warrior = state.warriors.find((w) => w.id === id);
  if (!warrior) throw new RuleError('UNKNOWN_PIECE', `No warrior ${id} on the ice`);
  return warrior;
}

export function sameIceAction(a: IceAction, b: IceAction): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Plays `action` for the side to move. Throws `RuleError` if it is not legal. */
export function applyIceAction(state: IceState, action: IceAction): IceResult {
  if (state.phase === 'over') throw new RuleError('GAME_OVER', 'The game is over');
  if (action.kind === 'move' || action.kind === 'shove') {
    const warrior = warriorById(state, action.id);
    if (warrior.side !== state.turn) {
      throw new RuleError('NOT_YOUR_TURN', `${action.id} belongs to the other side`);
    }
    if (action.kind === 'shove') {
      const option = shoveOptions(state, warrior).find((o) => sameIceAction(o.action, action));
      if (option && !option.legal) {
        throw new RuleError(
          'ILLEGAL_ACTION',
          `The shield wall holds: ${option.strength} against ${option.wall}`,
        );
      }
    }
  }
  if (!legalIceActions(state).some((legal) => sameIceAction(legal, action))) {
    throw new RuleError('ILLEGAL_ACTION', `Not a legal ${action.kind}`);
  }
  return applyTrustedIceAction(state, action);
}

/**
 * Plays an action already known to be legal (one from `legalIceActions`), without checking it
 * again. For bots and replays of trusted records: an illegal action gives undefined results.
 */
export function applyTrustedIceAction(state: IceState, action: IceAction): IceResult {
  const next: IceState = {
    ...state,
    heights: state.heights.map((row) => [...row]),
    warriors: state.warriors.map((w) => ({ ...w })),
    cracks: state.cracks.map((c) => ({ ...c })),
    moves: [...state.moves, toIceNotation(state, action)],
  };
  const events: IceEvent[] = [];
  const side = state.turn;

  switch (action.kind) {
    case 'place': {
      const n = next.warriors.filter((w) => w.side === side && w.type === action.type).length;
      const warrior = {
        id: `${side}-${action.type}-${n + 1}`,
        side,
        type: action.type,
        ...action.at,
      };
      next.warriors.push(warrior);
      events.push({ kind: 'placed', warrior: { ...warrior } });
      next.turn = otherSide(side);
      if (next.warriors.length === 2 * ARMY_SIZE) {
        next.phase = 'playing';
        next.turn = 'brand';
      }
      return { state: next, events };
    }
    case 'move': {
      const warrior = warriorById(next, action.id);
      events.push({
        kind: 'moved',
        id: warrior.id,
        path: pathTo(reach(state, warrior), action.to),
      });
      warrior.r = action.to.r;
      warrior.c = action.to.c;
      break;
    }
    case 'shove': {
      const warrior = warriorById(state, action.id);
      const walks = warrior.r !== action.from.r || warrior.c !== action.from.c;
      const walk = walks
        ? pathTo(reach(state, warrior), action.from)
        : [{ r: warrior.r, c: warrior.c }];
      if (walks) events.push({ kind: 'moved', id: warrior.id, path: walk });
      const option = shoveFrom(state, warrior, walk, action.target);
      if (!option) throw new RuleError('ILLEGAL_ACTION', 'Not a legal shove');
      const sunk = new Set(option.slides.filter((s) => s.sunk).map((s) => s.id));
      const landed = new Map(option.slides.map((s) => [s.id, s.to]));
      next.warriors = next.warriors
        .filter((w) => !sunk.has(w.id))
        .map((w) =>
          w.id === warrior.id ? { ...w, ...action.target } : { ...w, ...landed.get(w.id) },
        );
      events.push({
        kind: 'shoved',
        id: warrior.id,
        direction: option.direction,
        strength: option.strength,
        wall: option.wall,
        slides: option.slides,
      });
      break;
    }
    case 'pass':
      events.push({ kind: 'passed', side });
      break;
  }

  endTurn(next, side, events);
  return { state: next, events };
}

function finish(state: IceState, winner: Side, reason: IceWinReason, events: IceEvent[]): void {
  state.phase = 'over';
  state.winner = winner;
  state.winReason = reason;
  events.push({ kind: 'over', winner, reason });
}

/** Sinking every enemy wins at once. If both sides sink together, the attack has failed. */
function checkSunk(state: IceState, events: IceEvent[]): boolean {
  const brand = state.warriors.some((w) => w.side === 'brand');
  const rime = state.warriors.some((w) => w.side === 'rime');
  if (brand && rime) return false;
  finish(state, brand ? 'brand' : 'rime', 'sunk', events);
  return true;
}

function endTurn(state: IceState, side: Side, events: IceEvent[]): void {
  if (checkSunk(state, events)) return;
  const { config } = state;
  if (side === 'brand') {
    const gained = runestonesHeld(state);
    state.glory += gained;
    events.push({ kind: 'glory', gained, total: state.glory });
    if (state.glory >= config.gloryTarget) {
      finish(state, 'brand', 'glory', events);
    } else if (
      state.glory + MAX_GLORY_PER_TURN * (config.roundLimit - state.round) <
      config.gloryTarget
    ) {
      finish(state, 'rime', 'held', events);
    } else {
      state.turn = 'rime';
    }
    return;
  }
  endRound(state, events);
  if (checkSunk(state, events)) return;
  state.round++;
  state.turn = 'brand';
}

/** Thaws the cracks due this round, then, every `crackEvery` rounds, the ice groans. */
function endRound(state: IceState, events: IceEvent[]): void {
  const { round, config } = state;
  for (const crack of state.cracks.filter((c) => c.thawRound === round)) {
    const before = heightAt(state.heights, crack.r, crack.c);
    const height = before === 0 ? HOLE : before - 1;
    setHeight(state.heights, crack, height);
    let sunk: string | null = null;
    if (height === HOLE) {
      sunk = state.warriors.find((w) => w.r === crack.r && w.c === crack.c)?.id ?? null;
      state.warriors = state.warriors.filter((w) => w.id !== sunk);
    }
    events.push({ kind: 'thawed', r: crack.r, c: crack.c, height, sunk });
  }
  state.cracks = state.cracks.filter((c) => c.thawRound !== round);

  if (round % config.crackEvery !== 0) return;
  const rng = mulberry32((state.crackSeed ^ Math.imul(round, 0x9e3779b1)) >>> 0);
  for (let i = 0; i < config.cracksPerGroan; i++) {
    for (let attempt = 0; attempt < CRACK_ATTEMPTS; attempt++) {
      const crack: Crack = {
        r: randomInt(rng, SIZE),
        c: randomInt(rng, SIZE),
        thawRound: round + config.thawDelay,
      };
      if (canCrack(state, crack)) {
        state.cracks.push(crack);
        events.push({ kind: 'cracked', ...crack });
        break;
      }
    }
  }
}

/**
 * A square can crack unless it is a hole, already cracked, or its thaw (with every other pending
 * thaw) would cut a runestone off from either territory.
 */
export function canCrack(state: IceState, crack: Crack): boolean {
  if (isHole(state.heights, crack.r, crack.c)) return false;
  if (state.cracks.some((c) => c.r === crack.r && c.c === crack.c)) return false;
  const after = state.heights.map((row) => [...row]);
  for (const c of [...state.cracks, crack]) {
    if (heightAt(after, c.r, c.c) === 0) setHeight(after, c, HOLE);
  }
  return runestonesConnected(after);
}

/** `side` gives up: the other side wins at once. Either side may resign, on any turn. */
export function resignIce(state: IceState, side: Side): IceResult {
  if (state.phase === 'over') throw new RuleError('GAME_OVER', 'The game is over');
  const next: IceState = { ...state };
  const events: IceEvent[] = [];
  finish(next, otherSide(side), 'resign', events);
  return { state: next, events };
}

export function isIceGameOver(state: IceState): boolean {
  return state.phase === 'over';
}
